/**
 * Canonical LLM list for Guardians settings, Guardian A/B, and Agent A/B UIs.
 * Keep in sync with ``ani_api/app/core/guardiansv2/pricing.py`` (``MODEL_PRICING`` / ``GUARDIANS_LLM_MODEL_IDS``).
 */

export type LlmModelProvider = 'anthropic' | 'openai';

export interface AvailableModelOption {
  value: string;
  label: string;
  provider: LlmModelProvider;
}

/** Models available for selection in ops LLM pickers (Guardians + Agent testing). */
export const AVAILABLE_MODELS = [
  // Anthropic
  { value: 'claude-opus-4-8', label: 'Claude Opus 4.8', provider: 'anthropic' as const },
  { value: 'claude-opus-4-7', label: 'Claude Opus 4.7', provider: 'anthropic' as const },

  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', provider: 'anthropic' as const },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', provider: 'anthropic' as const },

  { value: 'claude-opus-4-5', label: 'Claude Opus 4.5', provider: 'anthropic' as const },
  { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', provider: 'anthropic' as const },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', provider: 'anthropic' as const },

  // OpenAI — GPT-5.6 (Sol / Terra / Luna); gpt-5.4-pro is intentionally omitted
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', provider: 'openai' as const },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', provider: 'openai' as const },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', provider: 'openai' as const },

  { value: 'gpt-5.5', label: 'GPT-5.5', provider: 'openai' as const },

  { value: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai' as const },
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini', provider: 'openai' as const },
  { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano', provider: 'openai' as const },

  { value: 'gpt-5', label: 'GPT-5', provider: 'openai' as const },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini', provider: 'openai' as const },
  { value: 'gpt-5-nano', label: 'GPT-5 Nano', provider: 'openai' as const },
] as const satisfies readonly AvailableModelOption[];

/** Case Checking–only models (not in Guardians pricing catalogue). */
export const CASE_CHECKING_EXTRA_MODELS = [
  { value: 'claude-opus-5', label: 'Claude Opus 5', provider: 'anthropic' as const },
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5', provider: 'anthropic' as const },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', provider: 'anthropic' as const },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', provider: 'anthropic' as const },
] as const satisfies readonly AvailableModelOption[];

const ALL_KNOWN_MODELS: readonly AvailableModelOption[] = [
  ...AVAILABLE_MODELS,
  ...CASE_CHECKING_EXTRA_MODELS,
];

/** Display label for a model id (Guardians catalogue + case-checking extras). */
export function modelLabel(id: string): string {
  return ALL_KNOWN_MODELS.find((m) => m.value === id)?.label ?? id;
}

/**
 * Resolves provider for a model id, using the catalogue first then OpenAI-style id heuristics.
 */
export function getProviderForModel(model: string): LlmModelProvider {
  const entry = ALL_KNOWN_MODELS.find((m) => m.value === model);
  if (entry) return entry.provider;
  return model.startsWith('gpt') || model.startsWith('o1') || model.startsWith('o3') || model.startsWith('o4')
    ? 'openai'
    : 'anthropic';
}

/** Grouped model ids for dropdowns that render by provider section. */
export const MODEL_OPTIONS_BY_PROVIDER: { provider: LlmModelProvider; models: string[] }[] = [
  {
    provider: 'anthropic',
    models: AVAILABLE_MODELS.filter((m) => m.provider === 'anthropic').map((m) => m.value),
  },
  {
    provider: 'openai',
    models: AVAILABLE_MODELS.filter((m) => m.provider === 'openai').map((m) => m.value),
  },
];
